/** Variantele de recompartimentare ale unui apartament, în inspector.
 *
 *  Jobul de recompartimentare scrie în plan cea mai bună compartimentare și păstrează
 *  cele mai bune trei (`pipeline.recomp_varianta`). Aici omul le vede una lângă alta —
 *  mini-planul, suprafața camerelor, ce verificare pică.
 *
 *  RĂSFOITE PE PLANUL ADEVĂRAT, nu doar în miniatură: click pe un card (sau ← →) și
 *  varianta se desenează peste apartament, pe loc (`onPrevizualizeaza`). «Aplică» o
 *  lasă pe plan imediat (`onAplicat`) și o scrie în document DIRECT, din delta ei deja
 *  calculată — secunde, fără solver, fără regenerare, fără job —, iar răspunsul aduce
 *  chiar obiectele scrise, îmbinate în documentul de pe ecran (`onScris`), fără recitire
 *  (Cătălin, 7.10.2026: «aplicare simplă, instant, fără flickering sau să mute camera»;
 *  30.10.2026: «să-mi arate în UI imediat»). Pe o soluție dinaintea deltelor scrierea e
 *  un job (`onJob`), urmărit până scrie. Cu „și pe cele la fel", aceeași variantă pe
 *  toată grupa de simetrie, și în celelalte corpuri; bifajul le arată pe plan.
 */
import { useCallback, useEffect, useState } from "react";
import * as api from "../api";
import { useT } from "../i18n";
import type { ApartmentFlat } from "./plan/ApartmentView";

const CULOARE: Record<string, string> = {
  living: "#f6efd9", bedroom: "#dce9f5", kitchen: "#fbe3cc", bath: "#d6ecf3",
  hall: "#f3e7c9", room: "#eeeeee", niche: "#e4e4e4",
};

function MiniPlan({ v }: { v: api.VariantaRecomp }) {
  const inele = v.camere.map((c) => c.poly).filter((r) => r.length >= 3);
  if (!inele.length) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const r of inele) for (const [x, y] of r) {
    x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
  }
  const w = Math.max(x1 - x0, 0.01), h = Math.max(y1 - y0, 0.01), pad = Math.max(w, h) * 0.05;
  const d = (r: number[][]) => r.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(3)},${p[1].toFixed(3)}`).join(" ") + " Z";
  return (
    <svg className="pg-recomp-fig" viewBox={`${x0 - pad} ${y0 - pad} ${w + 2 * pad} ${h + 2 * pad}`}
         preserveAspectRatio="xMidYMid meet" role="img" aria-label={v.tip}>
      {/* axa y a documentului crește în sus; SVG-ul în jos */}
      <g transform={`translate(0 ${y0 + y1}) scale(1 -1)`}>
        {v.camere.map((c, i) => c.poly.length >= 3 && (
          <path key={i} d={d(c.poly)} fill={CULOARE[c.fel] ?? "#eee"} stroke="#777"
                vectorEffect="non-scaling-stroke" strokeWidth={0.6} />
        ))}
        {v.pereti.map((r, i) => r.length >= 3 && (
          <path key={`p${i}`} d={d(r)} fill="#444" stroke="none" />
        ))}
        {v.usi.map((u, i) => u.length >= 2 && (
          <path key={`u${i}`} d={`M${u[0][0]},${u[0][1]} L${u[1][0]},${u[1][1]}`} stroke="#8e44ad"
                vectorEffect="non-scaling-stroke" strokeWidth={3} />
        ))}
      </g>
    </svg>
  );
}

/** Ce se desenează peste plan: un apartament și varianta lui (coordonatele documentului). */
export interface VariantaPePlan { eid: string; v: api.VariantaRecomp }

export default function RecompPanou({ projectId, solutionId, flat, onEvidentiaza,
                                      onPrevizualizeaza, onAplicat, onScris, onJob, onAnulat,
                                      aplicata }: {
  projectId: string; solutionId: string; flat: ApartmentFlat;
  /** apartamentele de evidențiat pe plan (oglinzile), sau `null` */
  onEvidentiaza: (ids: string[] | null) => void;
  /** variantele răsfoite, de desenat peste plan; `null` = nimic de arătat */
  onPrevizualizeaza: (p: VariantaPePlan[] | null) => void;
  /** omul a apăsat «Aplică»: variantele stau pe plan până le aduce documentul */
  onAplicat: (p: VariantaPePlan[], rang: number) => void;
  /** varianta s-a scris direct: documentul primește obiectele ei (`plan/recompDelta`) */
  onScris: (r: api.AplicareDirecta) => void;
  /** scrierea a pornit ca job (soluție dinaintea deltelor): se urmărește până scrie */
  onJob: () => void;
  /** cererea a picat: variantele apăsate ies de pe plan */
  onAnulat: (eids: string[]) => void;
  /** varianta aplicată pe apartamentul ăsta care încă se scrie (de la vizualizator) */
  aplicata: number | null;
}) {
  const t = useT();
  // TOATE rândurile soluției, nu doar al apartamentului: oglinzile își au variantele lor
  const [toate, setToate] = useState<Record<string, api.VarianteApartament> | null | undefined>(undefined);
  const [oglinzi, setOglinzi] = useState(false);
  const [vazut, setVazut] = useState<number | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [trimit, setTrimit] = useState(false);
  const eid = flat.eid ?? "";
  const inLucru = flat.recomp?.stare === "in_lucru";
  // RĂSPUNSUL RAPID al jobului: scris, dar căutarea întreagă mai rulează și îl rescrie
  const provizorie = flat.recomp?.stare === "provizoriu";
  const date = toate === undefined ? undefined : (toate?.[eid] ?? null);
  // ce e SCRIS în document acum (nu ce s-a cerut)
  const scrisa = flat.recomp?.ales ?? date?.ales ?? 0;

  useEffect(() => {
    let viu = true;
    setToate(undefined); setEroare(null); setOglinzi(false); setVazut(null);
    if (!eid) { setToate(null); return; }
    api.getRecompVariante(projectId, solutionId)
      .then((r) => { if (viu) setToate(r.apartamente); })
      .catch(() => { if (viu) setToate(null); });
    return () => { viu = false; };
  }, [projectId, solutionId, eid]);

  // bifajul oglinzilor le arată pe plan; se stinge cu panoul
  useEffect(() => {
    onEvidentiaza(oglinzi && date?.oglinzi?.length ? date.oglinzi : null);
    return () => onEvidentiaza(null);
  }, [oglinzi, date, onEvidentiaza]);

  /** Varianta `rang` pe apartament și, cu bifajul, pe oglinzile lui. */
  const pePlan = useCallback((rang: number): VariantaPePlan[] => {
    if (!date || !date.variante[rang]) return [];
    const out: VariantaPePlan[] = [{ eid, v: date.variante[rang] }];
    if (oglinzi) {
      for (const o of date.oglinzi) {
        const v = toate?.[o]?.variante[rang];
        if (v) out.push({ eid: o, v });
      }
    }
    return out;
  }, [date, eid, oglinzi, toate]);

  // RĂSFOIREA, pe plan: varianta văzută, dacă nu e chiar cea scrisă (aia e deja desenată)
  useEffect(() => {
    onPrevizualizeaza(vazut != null && vazut !== scrisa && vazut !== aplicata ? pePlan(vazut) : null);
  }, [vazut, scrisa, aplicata, pePlan, onPrevizualizeaza]);
  useEffect(() => () => onPrevizualizeaza(null), [onPrevizualizeaza]);

  // ← → trec prin variante (nu cât omul scrie într-un câmp)
  const n = date?.variante.length ?? 0;
  useEffect(() => {
    if (n < 2) return;
    const tasta = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const tinta = e.target as HTMLElement | null;
      if (tinta && (tinta.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(tinta.tagName))) return;
      e.preventDefault();
      // SĂGEȚILE SUNT ALE PANOULUI cât e deschis: planul le ia și el de pe
      // fereastră (ca W/A/S/D) și s-ar fi mutat sub variante. Ascultat la
      // captură, panoul le oprește înainte să ajungă la ceilalți.
      e.stopPropagation();
      setVazut((v) => {
        const acum = v ?? aplicata ?? scrisa;
        return (acum + (e.key === "ArrowRight" ? 1 : n - 1)) % n;
      });
    };
    window.addEventListener("keydown", tasta, true);
    return () => window.removeEventListener("keydown", tasta, true);
  }, [n, scrisa, aplicata]);

  if (!flat.recomp && date === null) return null;

  const aplica = async (rang: number) => {
    setEroare(null);
    setTrimit(true);
    const peste = pePlan(rang);
    // PE PLAN, PE LOC, înainte de răspuns: varianta stă desenată cât se scrie
    onAplicat(peste, rang);
    try {
      const r = await api.aplicaRecompVarianta(projectId, solutionId, eid, rang, oglinzi);
      // DIRECT: e deja scrisă, iar răspunsul poartă obiectele ei — documentul le primește
      // fără recitire. Prin JOB (soluție dinaintea deltelor): se urmărește până scrie.
      if (api.eAplicareDirecta(r)) onScris(r);
      else onJob();
      setVazut(rang);
    } catch (e) {
      onAnulat(peste.map((x) => x.eid));
      setEroare((e as { message?: string })?.message ?? t("recomp.eroare"));
    } finally {
      setTrimit(false);
    }
  };

  const curenta = vazut ?? aplicata ?? scrisa;
  return (
    <section className="pg-recomp">
      <h4>{t("recomp.titlu")}</h4>
      {inLucru && (
        <p className="pg-recomp-lucru"><span className="pg-recomp-spin" aria-hidden="true" />
          {t("recomp.in_lucru")}</p>
      )}
      {provizorie && (
        <p className="pg-recomp-lucru"><span className="pg-recomp-spin" aria-hidden="true" />
          {t("recomp.provizoriu")}</p>
      )}
      {flat.recomp?.stare === "esuat" && <p className="pg-inspect-warn">{t("recomp.esuat")}</p>}
      {date === undefined && !inLucru && <p className="pg-recomp-muted">{t("recomp.se_incarca")}</p>}
      {date === null && !inLucru && flat.recomp && <p className="pg-recomp-muted">{t("recomp.fara_variante")}</p>}
      {date && (
        <>
          {date.oglinzi.length > 0 && (
            <label className="pg-recomp-oglinzi">
              <input type="checkbox" checked={oglinzi} onChange={(e) => setOglinzi(e.target.checked)} />
              {t("recomp.si_pe_oglinzi", { n: date.oglinzi.length })}
            </label>
          )}
          {n > 1 && (
            <div className="pg-recomp-nav">
              <button type="button" aria-label={t("recomp.anterioara")}
                      onClick={() => setVazut((curenta + n - 1) % n)}>‹</button>
              <span>{t("recomp.varianta_n", { n: curenta + 1, m: n })}</span>
              <button type="button" aria-label={t("recomp.urmatoarea")}
                      onClick={() => setVazut((curenta + 1) % n)}>›</button>
            </div>
          )}
          <div className="pg-recomp-carduri">
            {date.variante.map((v) => {
              const eScrisa = v.rang === scrisa && aplicata == null;
              const seScrie = v.rang === aplicata;
              const rele = v.verificari.filter((x) => x.ok === false);
              const praguri = v.praguri.filter((x) => x.ok === false);
              return (
                <div key={v.rang} role="button" tabIndex={0}
                     className={"pg-recomp-card" + (eScrisa || seScrie ? " aleasa" : "")
                                + (v.rang === curenta ? " vazuta" : "")}
                     onClick={() => setVazut(v.rang)}
                     onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setVazut(v.rang); } }}>
                  <MiniPlan v={v} />
                  <div className="pg-recomp-rand"><b>{v.tip}</b>
                    <span>{t("recomp.camere_m2", { m2: v.camere_m2.toFixed(1) })}</span></div>
                  <div className="pg-recomp-rand pg-recomp-muted">
                    <span>{t("recomp.util_m2", { m2: v.util_m2.toFixed(1) })}</span>
                    {praguri.length > 0 && <span>{t("recomp.praguri", { n: praguri.length })}</span>}
                  </div>
                  {rele.length > 0 && (
                    <div className="pg-inspect-warn pg-recomp-rand">{rele.map((x) => x.cheie).join(" · ")}</div>
                  )}
                  <button type="button" className="pg-recomp-aplica"
                          disabled={eScrisa || seScrie || aplicata != null || trimit || inLucru}
                          onClick={(e) => { e.stopPropagation(); void aplica(v.rang); }}>
                    {eScrisa ? t("recomp.scrisa") : seScrie ? t("recomp.trimisa") : t("recomp.aplica")}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
      {eroare && <p className="pg-inspect-warn">{eroare}</p>}
    </section>
  );
}
